import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useStillMounted } from '../../shared/recordScoped';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  AVIZATION_RESULTS,
  CONTACT_CHANNELS,
  FEATURE_CS_LIGHT,
  MIN_NOTE_TEXT,
  addClientContact,
  fetchClientContacts,
  toErrorMessage,
  type ClientContact,
} from './csWorkApi';

/**
 * Wynik awizacji i notatki przy zleceniu — sekcja 7 okna zlecenia (paczka
 * p. Mariusza 21.10, BR-CS-10; przełącznik `csLight`, S-21.10-16). CS
 * wpisuje ręcznie: kanał (telefon, mail, platforma), wynik i notatkę albo
 * samą notatkę. Kto i kiedy — zapisuje API. Nic nie jest wysyłane.
 */
export const ClientContactLog = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const on = useFeature(FEATURE_CS_LIGHT);
  const id = record?.id;
  const [items, setItems] = useState<ClientContact[]>([]);
  const [canWrite, setCanWrite] = useState(false);
  const [kind, setKind] = useState<'avization' | 'note'>('avization');
  const [channel, setChannel] = useState('');
  const [result, setResult] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!on || id === undefined || id === null) return;
    fetchClientContacts(id)
      .then((r) => {
        if (!stillMounted()) return;
        setItems(r.items);
        setCanWrite(r.canWrite);
      })
      .catch(() => undefined);
  }, [on, id, stillMounted]);

  useEffect(load, [load]);

  if (!on || id === undefined || id === null) return null;

  const noteOk = text.trim().length >= MIN_NOTE_TEXT;
  const ready =
    kind === 'note' ? noteOk : channel !== '' && result !== '' && (result !== 'other' || noteOk);

  const save = async () => {
    setBusy(true);
    try {
      await addClientContact(
        id,
        kind === 'note'
          ? { kind, text }
          : { kind, channel, result, ...(text.trim() ? { text } : {}) },
      );
      notify('Zapisano przy zleceniu', { type: 'success' });
      setChannel('');
      setResult('');
      setText('');
      load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  return (
    <Box data-testid="client-contact-log" sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        Awizacje i notatki CS
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak wpisów — wynik awizacji i notatki wpisuje CS.
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((c) => (
            <li key={c.id} data-testid="client-contact-entry">
              {/* Kontrakt `data-testid` paczki (S-22.10-02): wiersz awizacji — u nas wpis CS na zleceniu. */}
              <Typography variant="body2" data-testid={c.kind === 'avization' ? `cs-awizacje-wiersz-${c.id}` : undefined}>
                {c.kind === 'avization'
                  ? `Awizacja (${c.channelLabel ?? '—'}): ${c.resultLabel ?? '—'}`
                  : 'Notatka'}
                {c.text ? ` — ${c.text}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[c.createdByName, dateTimeNoSeconds(c.createdAt)].filter(Boolean).join(', ')}
              </Typography>
            </li>
          ))}
        </Box>
      )}
      {canWrite ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Box
            sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))' }}
          >
            <TextField
              select
              size="small"
              label="Wpis"
              value={kind}
              disabled={busy}
              onChange={(e) => setKind(e.target.value as 'avization' | 'note')}
              slotProps={{ htmlInput: { 'data-testid': 'client-contact-kind' } }}
            >
              <MenuItem value="avization">wynik awizacji</MenuItem>
              <MenuItem value="note">notatka</MenuItem>
            </TextField>
            {kind === 'avization' ? (
              <>
                <TextField
                  select
                  size="small"
                  label="Kanał"
                  value={channel}
                  disabled={busy}
                  onChange={(e) => setChannel(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'client-contact-channel' } }}
                >
                  {CONTACT_CHANNELS.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Wynik"
                  value={result}
                  disabled={busy}
                  onChange={(e) => setResult(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'client-contact-result' } }}
                >
                  {AVIZATION_RESULTS.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
          </Box>
          <TextField
            size="small"
            label={kind === 'note' || result === 'other' ? 'Notatka' : 'Notatka (opcjonalnie)'}
            value={text}
            multiline
            minRows={2}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'client-contact-text' } }}
          />
          <Box>
            <Button
              size="small"
              variant="contained"
              disabled={busy || !ready}
              onClick={() => void save()}
              data-testid="client-contact-save"
            >
              Zapisz przy zleceniu
            </Button>
          </Box>
          <Alert severity="info" variant="outlined">
            Z systemu nic nie wychodzi — zapisujesz, co ustaliłeś z klientem. Wpis widzi FTL i Trade.
          </Alert>
        </Stack>
      ) : null}
    </Box>
  );
};
